/**
 * Opportunity Form Page
 *
 * Create a new opportunity.
 *
 * @module pages/opportunities/OpportunityForm
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from 'react-bootstrap';
import { toast } from '../../utils/toast';
import { createOpportunity } from '../../services/opportunityService';
import { listClients } from '../../services/clientService';
import DashboardLayout from '../../components/layout/DashboardLayout';

const SOURCES = [
  { value: 'referral', label: 'Referencia' },
  { value: 'website', label: 'Sitio Web' },
  { value: 'cold_call', label: 'Llamada en frío' },
  { value: 'trade_show', label: 'Feria Comercial' },
  { value: 'repeat_client', label: 'Cliente Recurrente' },
  { value: 'other', label: 'Otro' },
];

const OpportunityForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    client_id: '',
    estimated_value: '',
    source: '',
    expected_close_date: '',
    notes: '',
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { pageSize: 200 }],
    queryFn: () => listClients({ pageSize: 200 }),
  });

  const clients = clientsData?.data || [];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const data = {
        ...formData,
        client_id: formData.client_id || null,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        expected_close_date: formData.expected_close_date || null,
      };
      const result = await createOpportunity(data);
      if (result.error) throw result.error;
      toast.success('Oportunidad creada exitosamente');
      queryClient.invalidateQueries(['opportunities']);
      navigate(`/oportunidades/${result.data.id}`);
    } catch (err) {
      console.error('Create opportunity error:', err);
      toast.error('Error al crear oportunidad');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <Button
              variant="link"
              className="p-0 mb-2 text-muted"
              onClick={() => navigate('/oportunidades')}
            >
              <i className="bi bi-arrow-left me-1"></i>Volver a Oportunidades
            </Button>
            <h2>Nueva Oportunidad</h2>
          </Col>
        </Row>

        <Row>
          <Col lg={8}>
            <Card>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Título *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      required
                      placeholder="Ej: Mantenimiento preventivo Hospital XYZ"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Detalles de la oportunidad..."
                    />
                  </Form.Group>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Cliente</Form.Label>
                        <Form.Select
                          value={formData.client_id}
                          onChange={(e) => handleChange('client_id', e.target.value)}
                        >
                          <option value="">Seleccionar cliente...</option>
                          {clients.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.company_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Fuente</Form.Label>
                        <Form.Select
                          value={formData.source}
                          onChange={(e) => handleChange('source', e.target.value)}
                        >
                          <option value="">Seleccionar...</option>
                          {SOURCES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        {/* #617: sin "(USD)". `opportunities` no tiene columna de moneda. */}
                        <Form.Label>Valor Estimado</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.estimated_value}
                          onChange={(e) => handleChange('estimated_value', e.target.value)}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Fecha de Cierre Esperada</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.expected_close_date}
                          onChange={(e) => handleChange('expected_close_date', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label>Notas</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Notas adicionales..."
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" disabled={saving || !formData.title.trim()}>
                      {saving ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" className="me-1" />
                          Creando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-plus-circle me-1"></i>
                          Crear Oportunidad
                        </>
                      )}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/oportunidades')} disabled={saving}>
                      Cancelar
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
};

export default OpportunityForm;
